import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	SearchableSelect,
	type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import { type Project } from "@/services/project.service";
import { type Sprint } from "@/services/sprint.service";
import { type Profile } from "@/services/profile.service";
import { getStageLabel } from "@/components/tasks/types";

export interface TaskReportsFilterValues {
	projectId: string;
	sprintId: string;
	assignedTo: string;
	status: string;
	dateFrom: string;
	dateTo: string;
	search: string;
}

const inputClass =
	"h-9 rounded-lg border border-border-strong bg-surface px-3 text-sm text-foreground";

export default function TaskReportsFilters({
	values,
	onChange,
	onClear,
	projects,
	sprints,
	users,
	statuses,
}: {
	values: TaskReportsFilterValues;
	onChange: (patch: Partial<TaskReportsFilterValues>) => void;
	onClear: () => void;
	projects: Project[];
	sprints: Sprint[];
	users: Profile[];
	statuses: string[];
}) {
	const projectOptions: SearchableSelectOption[] = projects.map((p) => ({
		value: p.id,
		label: p.name,
	}));
	const sprintOptions: SearchableSelectOption[] = sprints.map((s) => ({
		value: s.id,
		label: s.name,
	}));
	const userOptions: SearchableSelectOption[] = users.map((u) => ({
		value: u.id,
		label: u.full_name ?? u.email,
		description: u.full_name ? u.email : undefined,
	}));
	const statusOptions: SearchableSelectOption[] = statuses.map((s) => ({
		value: s,
		label: getStageLabel(s),
	}));

	const hasFilters =
		values.projectId ||
		values.sprintId ||
		values.assignedTo ||
		values.status ||
		values.dateFrom ||
		values.dateTo ||
		values.search;

	return (
		<div className="flex flex-col gap-3">
			<div className="relative flex items-center gap-4">
				<Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
				<input
					value={values.search}
					onChange={(e) => onChange({ search: e.target.value })}
					placeholder="Search tasks…"
					className="h-9 w-1/3 rounded-lg border border-border-strong bg-surface pl-8 pr-3 text-sm text-foreground"
				/>
				<SearchableSelect
					className="w-50"
					value={values.projectId}
					onValueChange={(v) => onChange({ projectId: v })}
					options={projectOptions}
					placeholder="All projects"
					clearable
					ariaLabel="Filter by project"
					renderOption={(opt) => (
						<div className="flex items-center gap-2">
							<div>
								<div className="text-xs font-medium">
									{opt.label}
								</div>
							</div>
						</div>
					)}
				/>
				<SearchableSelect
					value={values.sprintId}
					onValueChange={(v) => onChange({ sprintId: v })}
					options={sprintOptions}
					placeholder="All sprints"
					clearable
					ariaLabel="Filter by sprint"
				/>
				<SearchableSelect
					value={values.assignedTo}
					onValueChange={(v) => onChange({ assignedTo: v })}
					options={userOptions}
					placeholder="All assignees"
					clearable
					ariaLabel="Filter by assignee"
					renderOption={(opt) => (
						<div className="flex items-center gap-2">
							<div>
								<div className="text-xs font-medium">
									{opt.label}
								</div>
								{opt.description && (
									<div className="text-[8px] text-muted truncate overflow-hidden line-clamp-1">
										{String(opt.description).split("@")[0]}
									</div>
								)}
							</div>
						</div>
					)}
				/>
				<SearchableSelect
					value={values.status}
					onValueChange={(v) => onChange({ status: v })}
					options={statusOptions}
					placeholder="All statuses"
					clearable
					ariaLabel="Filter by status"
				/>
			</div>

			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<div className="flex items-center gap-2">
					<label className="text-xs text-muted">Created from</label>
					<input
						type="date"
						value={values.dateFrom}
						max={values.dateTo || undefined}
						onChange={(e) => onChange({ dateFrom: e.target.value })}
						className={inputClass}
					/>
				</div>
				<div className="flex items-center gap-2">
					<label className="text-xs text-muted">to</label>
					<input
						type="date"
						value={values.dateTo}
						min={values.dateFrom || undefined}
						onChange={(e) => onChange({ dateTo: e.target.value })}
						className={inputClass}
					/>
				</div>
				{hasFilters && (
					<Button
						variant="outline"
						size="sm"
						onClick={onClear}
						className="sm:ml-auto"
					>
						<X className="h-4 w-4" />
						Clear
					</Button>
				)}
			</div>
		</div>
	);
}
